// WHAT: Donut chart component for displaying time by workspace
// WHY:  Provides visual breakdown of time spent per workspace with color coding
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: recharts for charting, useFilter, useWorkspaceConfig hooks
// RISKS:
//   - NOTE: Workspace aggregation from aspect entries may be incorrect
//   - NOTE: Color assignment may not match workspace colors from config
//   - NOTE: Normalization function may cause issues with special characters

import type { JSX } from "react";

import { core } from "@tauri-apps/api";
import { useState, useEffect, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";

import { Card } from "@/components/ui";
import { useFilter } from "@/domains/productivity/timetracq/components/molecules/FilterContextProvider.shared";
import { useWorkspaceConfig } from "@/domains/productivity/timetracq/hooks/time_tracking/useWorkspaceConfig";

/**
 * Aspect entry interface (from backend)
 */
interface AspectEntry {
  /** Aspect label (workspace/aspect format) */
  label: string;
  /** Minutes */
  minutes: number;
}

/**
 * Workspace slice interface (for chart)
 */
interface WorkspaceSlice {
  /** Display color */
  color: string;
  /** Workspace name */
  label: string;
  /** Minutes */
  minutes: number;
}

/**
 * Backend response interface
 */
interface BackendResponse {
  /** Category chart data */
  category_chart: AspectEntry[];
}

/**
 * WHAT:
 *   Normalizes string for comparison (trim and lowercase).
 *
 * WHY:
 *   Enables consistent workspace name matching.
 *
 * @param s - String to normalize
 *
 * @returns Normalized string
 *
 */
const normalize = (s: string): string => s.trim().toLowerCase();

/**
 * WHAT:
 *   Donut chart component that displays time spent per workspace with color
 *   coding based on workspace configuration.
 *
 * WHY:
 *   Enables users to visualize time distribution across workspaces in a
 *   compact donut chart format.
 *
 * BEHAVIOR:
 *   - Fetches aggregated data from backend
 *   - Aggregates aspect entries by workspace
 *   - Displays donut chart with workspace breakdown
 *   - Colors slices based on workspace config
 *   - Updates when filter changes
 *
 * RISKS:
 *   - NOTE: Workspace aggregation from aspect entries may be incorrect
 *   - NOTE: Color assignment may not match workspace colors from config
 *   - NOTE: Normalization function may cause issues with special characters
 */
type WorkspaceConfigMap = Record<string, Record<string, { color?: string }>>;

type UseFilterFunction = () => unknown;
const safeUseFilter: UseFilterFunction = useFilter as UseFilterFunction;

type UseWorkspaceConfigFunction = () => unknown;
const safeUseWorkspaceConfig: UseWorkspaceConfigFunction =
  useWorkspaceConfig as UseWorkspaceConfigFunction;

interface FilterLike {
  // Structure of dateFilter is handled by backend; we only pass it through
  // to the Tauri command, so we keep it loosely typed here.
  dateFilter: unknown;
  period: string;
}

const isFilterLike = (value: unknown): value is FilterLike => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record.period === "string" && "dateFilter" in record;
};

/**
 * WHAT:
 *   Donut chart component that displays time spent per workspace.
 *
 * @returns JSX.Element
 *
 */
export function CategoryDonutChart() {
  const rawFilter = safeUseFilter();
  const { dateFilter, period } = isFilterLike(rawFilter)
    ? rawFilter
    : { dateFilter: undefined, period: "All" };

  const [data, setData] = useState<AspectEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /* ─────────────────── Backend laden ─────────────────── */
  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(undefined);
      try {
        const res = await core.invoke<BackendResponse>("get_aggregated_summary", {
          filter: dateFilter,
        });
        setData(res.category_chart);
      } catch {
        // Donut chart load error
        setError("Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    })();
  }, [dateFilter]);

  /* ─────────── Aspect-Minuten ➜ Workspace-Slices ─────────── */
  const workspaceConfigRaw = safeUseWorkspaceConfig();

  // Typed workspace config + Ladezustand aus Hook in ein stabiles Objekt mappen
  const { cfgLoading, typedCfg } = useMemo(() => {
    let currentCfg: WorkspaceConfigMap = {};
    let currentLoading = true;

    if (
      Array.isArray(workspaceConfigRaw) &&
      workspaceConfigRaw.length >= 3 &&
      workspaceConfigRaw[0] &&
      typeof workspaceConfigRaw[0] === "object"
    ) {
      currentCfg = workspaceConfigRaw[0] as WorkspaceConfigMap;
      currentLoading = Boolean(workspaceConfigRaw[2]);
    }

    return { cfgLoading: currentLoading, typedCfg: currentCfg };
  }, [workspaceConfigRaw]);

  const slices: WorkspaceSlice[] = useMemo(() => {
    if (cfgLoading || Object.keys(typedCfg).length === 0) {
      return [];
    }

    const minutesPerWs: Record<string, number> = {};

    for (const { label, minutes } of data) {
      const aspectNorm = normalize(label);

      // passenden Workspace finden, der dieses Aspect führt
      const wsMatch = Object.keys(typedCfg).find((ws) =>
        Object.keys(typedCfg[ws] ?? {}).some((asp) => normalize(asp) === aspectNorm),
      );
      if (wsMatch) {
        minutesPerWs[wsMatch] = (minutesPerWs[wsMatch] ?? 0) + minutes;
      }
    }

    const result = Object.entries(minutesPerWs).map(([ws, minutes]) => {
      // Farbe = Farbe des ersten Aspects (oder Grau)
      const wsConfig = typedCfg[ws];
      const firstAsp = wsConfig
        ? (Object.values(wsConfig)[0] as undefined | { color?: string })
        : undefined;
      const color = firstAsp?.color ?? "#888888";
      return { color, label: ws, minutes };
    });

    return result;
  }, [data, typedCfg, cfgLoading]);

  const totalMinutes = slices.reduce((sum, s) => sum + s.minutes, 0);

  /**
   * WHAT:
   *   Returns chart content based on loading, error, or data state.
   *
   * @returns JSX.Element for chart content
   *
   */
  const getChartContent = (): JSX.Element => {
    if (loading || cfgLoading) {
      return (
        <div className="h-64 flex items-center justify-center text-slate-500 animate-pulse">
          Loading…
        </div>
      );
    }

    if (error) {
      return <div className="h-64 flex items-center justify-center text-red-400">{error}</div>;
    }

    if (slices.length === 0) {
      return (
        <div className="h-64 flex items-center justify-center text-slate-300">
          Keine Daten für den gewählten Zeitraum
        </div>
      );
    }

    return (
      <ResponsiveContainer height={260} width="100%">
        <PieChart>
          <Pie
            data={slices}
            dataKey="minutes"
            innerRadius={60}
            isAnimationActive={false}
            nameKey="label"
            outerRadius={100}
          >
            {slices.map((s) => (
              <Cell fill={s.color} key={s.label} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: "#1E293B",
              border: "1px solid #334155",
              borderRadius: "8px",
              color: "#FFFFFF !important",
              fontSize: "12px",
              fontWeight: "500",
              padding: "8px 12px",
            }}
            formatter={(v: number) => {
              const h = Math.floor(v / 60);
              const m = v % 60;
              const pct = totalMinutes ? ((v / totalMinutes) * 100).toFixed(2) : "0";
              return `${String(h)}h ${String(m)}m (${pct}%)`;
            }}
            labelStyle={{
              color: "#FFFFFF !important",
              fontSize: "12px",
              fontWeight: "600",
            }}
            wrapperStyle={{
              color: "#FFFFFF !important",
            }}
          />
          <Legend
            align="center"
            formatter={(v: string) => <span style={{ color: "#94A3B8" }}>{v}</span>}
            iconType="square"
            layout="horizontal"
            verticalAlign="bottom"
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  /* ────────────────────────── Render ───────────────────────── */
  return (
    <Card
      className="bg-slate-800/30 border-slate-600/50 shadow-tracq-card"
      padding="lg"
      variant="elevated"
    >
      <div className="space-y-4">
        <div className="text-sm text-slate-400">Workspaces ({period})</div>

        {getChartContent()}
      </div>
    </Card>
  );
}
